/**
 * Axios interceptor for Playground Mode.
 * Intercepts all outbound HTTP requests when playgroundMode is active,
 * matches URL patterns against a route table, and returns mock responses
 * from sessionStorage. No real network calls are made.
 */
import axios from 'axios';

// ── Handler imports ──────────────────────────────────────────────────
import {
    handleGetRemoteConfigs, handleGetRemoteConfigById, handleCreateRemoteConfig,
    handleUpdateRemoteConfig, handleDeleteRemoteConfig, handleCloneRemoteConfig,
    handleRollbackRemoteConfig, handleGetRemoteConfigVersions,
} from './handlers/remoteConfigHandlers.js';

import {
    handleGetAbTests, handleGetAbTestById, handleCreateAbTest,
    handleUpdateAbTest, handleDeleteAbTest, handleCloneAbTest,
    handleRollbackAbTest, handleGetAbTestVersions,
} from './handlers/abTestingHandlers.js';

import {
    handleGetSegments, handleGetActiveSegments, handleGetSegmentById, handleCreateSegment,
    handleUpdateSegment, handleDeleteSegment, handleDeleteSegmentTree, handleCloneSegment,
    handleRollbackSegment, handleGetSegmentVersions, handleGetMatchingCount,
} from './handlers/segmentationHandlers.js';

import {
    handleGetClientVersions, handleCreateClientVersion, handleUpdateClientVersion,
    handleGetGameFeatures, handleCreateGameFeature, handleUpdateGameFeature,
    handleGetPlayerAttributes, handleCreatePlayerAttribute, handleUpdatePlayerAttribute,
    handleDeletePlayerAttribute, handleRenamePlayerAttribute, handleMovePlayerAttribute,
    handleGetAttributeCategories, handleCreateAttributeCategory, handleUpdateAttributeCategory,
    handleDeleteAttributeCategory, handleGetAllAttributeData,
    handleGetContentTemplates, handleGetContentTemplate, handleCreateContentTemplate,
    handleUpdateContentTemplate, handleDeleteContentTemplate,
} from './handlers/metadataHandlers.js';

import {
    handleGetConfigFiles, handleGetConfigFile, handleCreateConfigFile, handleUpdateConfigFile,
    handleArchiveConfigFile, handleDeleteConfigFile, handleGetConfigBranches, handleGetConfigAudience,
    handleCreateConfigBranch, handleGetConfigBranch, handleSaveConfigBranch, handlePublishConfigBranch,
    handlePauseConfigBranch, handleResumeConfigBranch, handleSupersedeConfigBranch,
    handleUpdateConfigRollout, handleGetConfigBranchVersions, handleRollbackConfigBranch,
    handleDeleteConfigBranch, handleDiscoverConfigSystems, handleAutofillConfigFile,
    handlePreviewConfigSave,
    handleDismissConfigReview,
    handleResolveConfigPreview,
} from './handlers/gameConfigHandlers.js';

import {
    handleGetGames, handleGetGamesByOrgId, handleGetGameById, handleGetGameByClientId,
    handleCreateGame, handleUpdateGame, handleDeleteGame,
    handleSuspendGame, handleActivateGame, handleSunsetGame,
    handleGetSecretKeys, handleRotateSecret, handleRevokeSecret,
} from './handlers/gameManagementHandlers.js';

import {
    handleGetOrgs, handleGetOrgById, handleCreateOrg,
    handleUpdateOrg, handleArchiveOrg,
} from './handlers/orgManagementHandlers.js';

import {
    handleGetAllUsers, handleGetCurrentUser,
    handleGetInvitations, handleCreateInvitation, handleUpdateInvitation, handleDeleteInvitation,
} from './handlers/userManagementHandlers.js';

import {
    handleGetPortfolio, handleGetUserGames, handleGetCompanyByOrgId,
    handleGetCompanyGames, handleGetLiveServicesHomepage,
} from './handlers/dashboardHandlers.js';

import {
    handleGetTags, handleCreateTag, handleDeleteTag,
    handleGetRoles, handleGetPlatforms, handleGetUserPreferences,
    handleGetMetrics, handleGetPermissions, handleGetPages,
} from './handlers/commonHandlers.js';

// ── Route table ──────────────────────────────────────────────────────
// Order matters: more specific patterns must come before generic ones.
const routeTable = [
    // ── Remote Config ────────────────────────────────────────────────
    { method: 'GET',    pattern: /\/v1\/web\/remote-config\/([^/]+)\/versions$/,                handler: handleGetRemoteConfigVersions },
    { method: 'POST',   pattern: /\/v1\/web\/remote-config\/([^/]+)\/rollback/,                 handler: handleRollbackRemoteConfig },
    { method: 'POST',   pattern: /\/v1\/web\/remote-config\/([^/]+)\/clone$/,                   handler: handleCloneRemoteConfig },
    { method: 'GET',    pattern: /\/v1\/web\/remote-config\/([^/]+)$/,                          handler: handleGetRemoteConfigById },
    { method: 'PUT',    pattern: /\/v1\/web\/remote-config\/([^/]+)$/,                          handler: handleUpdateRemoteConfig },
    { method: 'DELETE', pattern: /\/v1\/web\/remote-config\/([^/]+)$/,                          handler: handleDeleteRemoteConfig },
    { method: 'GET',    pattern: /\/v1\/web\/remote-config$/,                                   handler: handleGetRemoteConfigs },
    { method: 'POST',   pattern: /\/v1\/web\/remote-config$/,                                   handler: handleCreateRemoteConfig },

    // ── A/B Testing ──────────────────────────────────────────────────
    { method: 'GET',    pattern: /\/v1\/web\/ab-test\/([^/]+)\/versions$/,                      handler: handleGetAbTestVersions },
    { method: 'POST',   pattern: /\/v1\/web\/ab-test\/([^/]+)\/rollback/,                       handler: handleRollbackAbTest },
    { method: 'POST',   pattern: /\/v1\/web\/ab-test\/([^/]+)\/clone$/,                         handler: handleCloneAbTest },
    { method: 'GET',    pattern: /\/v1\/web\/ab-test\/([^/]+)$/,                                handler: handleGetAbTestById },
    { method: 'PUT',    pattern: /\/v1\/web\/ab-test\/([^/]+)$/,                                handler: handleUpdateAbTest },
    { method: 'DELETE', pattern: /\/v1\/web\/ab-test\/([^/]+)$/,                                handler: handleDeleteAbTest },
    { method: 'GET',    pattern: /\/v1\/web\/ab-test$/,                                         handler: handleGetAbTests },
    { method: 'POST',   pattern: /\/v1\/web\/ab-test$/,                                         handler: handleCreateAbTest },

    // ── Segmentation ─────────────────────────────────────────────────
    { method: 'POST',   pattern: /\/v1\/web\/player-segment\/matching-count/,                   handler: handleGetMatchingCount },
    { method: 'GET',    pattern: /\/v1\/web\/player-segment\/active$/,                          handler: handleGetActiveSegments },
    { method: 'GET',    pattern: /\/v1\/web\/player-segment\/([^/]+)\/versions$/,               handler: handleGetSegmentVersions },
    { method: 'POST',   pattern: /\/v1\/web\/player-segment\/([^/]+)\/rollback/,                handler: handleRollbackSegment },
    { method: 'POST',   pattern: /\/v1\/web\/player-segment\/([^/]+)\/clone$/,                  handler: handleCloneSegment },
    { method: 'GET',    pattern: /\/v1\/web\/player-segment\/([^/]+)$/,                         handler: handleGetSegmentById },
    { method: 'PUT',    pattern: /\/v1\/web\/player-segment\/([^/]+)$/,                         handler: handleUpdateSegment },
    { method: 'DELETE', pattern: /\/v1\/web\/player-segment\/([^/]+)\/tree$/,                   handler: handleDeleteSegmentTree },
    { method: 'DELETE', pattern: /\/v1\/web\/player-segment\/([^/]+)$/,                         handler: handleDeleteSegment },
    { method: 'GET',    pattern: /\/v1\/web\/player-segment$/,                                  handler: handleGetSegments },
    { method: 'POST',   pattern: /\/v1\/web\/player-segment$/,                                  handler: handleCreateSegment },

    // ── Client Versions ──────────────────────────────────────────────
    { method: 'PUT',    pattern: /\/v1\/web\/client-version\/([^/]+)$/,                         handler: handleUpdateClientVersion },
    { method: 'GET',    pattern: /\/v1\/web\/client-version/,                                   handler: handleGetClientVersions },
    { method: 'POST',   pattern: /\/v1\/web\/client-version/,                                   handler: handleCreateClientVersion },

    // ── Game Features ────────────────────────────────────────────────
    { method: 'POST',   pattern: /\/v1\/web\/game-feature\/discover$/,                       handler: handleDiscoverConfigSystems },
    { method: 'PUT',    pattern: /\/v1\/web\/game-feature\/([^/]+)$/,                           handler: handleUpdateGameFeature },
    { method: 'GET',    pattern: /\/v1\/web\/game-feature/,                                     handler: handleGetGameFeatures },
    { method: 'POST',   pattern: /\/v1\/web\/game-feature/,                                     handler: handleCreateGameFeature },

    // ── Player Attributes ────────────────────────────────────────────
    { method: 'GET',    pattern: /\/v1\/web\/player-attribute-mgmt\/all/,                       handler: handleGetAllAttributeData },
    { method: 'PUT',    pattern: /\/v1\/web\/player-attributes\/([^/]+)\/rename/,               handler: handleRenamePlayerAttribute },
    { method: 'PUT',    pattern: /\/v1\/web\/player-attributes\/([^/]+)\/move/,                 handler: handleMovePlayerAttribute },
    { method: 'PUT',    pattern: /\/v1\/web\/player-attributes\/([^/]+)$/,                      handler: handleUpdatePlayerAttribute },
    { method: 'DELETE', pattern: /\/v1\/web\/player-attributes\/([^/]+)$/,                      handler: handleDeletePlayerAttribute },
    { method: 'GET',    pattern: /\/v1\/web\/player-attributes/,                                handler: handleGetPlayerAttributes },
    { method: 'POST',   pattern: /\/v1\/web\/player-attributes/,                                handler: handleCreatePlayerAttribute },

    // ── Attribute Categories ─────────────────────────────────────────
    { method: 'PUT',    pattern: /\/v1\/web\/player-attribute-categories\/([^/]+)$/,            handler: handleUpdateAttributeCategory },
    { method: 'DELETE', pattern: /\/v1\/web\/player-attribute-categories\/([^/]+)$/,            handler: handleDeleteAttributeCategory },
    { method: 'GET',    pattern: /\/v1\/web\/player-attribute-categories/,                      handler: handleGetAttributeCategories },
    { method: 'POST',   pattern: /\/v1\/web\/player-attribute-categories/,                      handler: handleCreateAttributeCategory },

    // ── Content Templates ────────────────────────────────────────────
    { method: 'GET',    pattern: /\/v1\/web\/content-template\/([^/]+)$/,                       handler: handleGetContentTemplate },
    { method: 'PUT',    pattern: /\/v1\/web\/content-template\/([^/]+)$/,                       handler: handleUpdateContentTemplate },
    { method: 'DELETE', pattern: /\/v1\/web\/content-template\/([^/]+)$/,                       handler: handleDeleteContentTemplate },
    { method: 'GET',    pattern: /\/v1\/web\/content-template/,                                 handler: handleGetContentTemplates },
    { method: 'POST',   pattern: /\/v1\/web\/content-template/,                                 handler: handleCreateContentTemplate },

    // ── Game Config ──────────────────────────────────────────────────
    // Ahead of /files/:fileId — "autofill" is a literal segment, not an id.
    { method: 'POST',   pattern: /\/v1\/web\/game-config\/files\/autofill$/,                     handler: handleAutofillConfigFile },
    { method: 'POST',   pattern: /\/v1\/web\/game-config\/files\/([^/]+)\/archive/,             handler: handleArchiveConfigFile },
    { method: 'GET',    pattern: /\/v1\/web\/game-config\/files\/([^/]+)\/branches$/,           handler: handleGetConfigBranches },
    { method: 'POST',   pattern: /\/v1\/web\/game-config\/files\/([^/]+)\/branches$/,           handler: handleCreateConfigBranch },
    { method: 'GET',    pattern: /\/v1\/web\/game-config\/files\/([^/]+)\/audience$/,           handler: handleGetConfigAudience },
    { method: 'POST',   pattern: /\/v1\/web\/game-config\/files\/([^/]+)\/resolve-preview$/,    handler: handleResolveConfigPreview },
    { method: 'GET',    pattern: /\/v1\/web\/game-config\/files\/([^/]+)$/,                      handler: handleGetConfigFile },
    { method: 'PUT',    pattern: /\/v1\/web\/game-config\/files\/([^/]+)$/,                      handler: handleUpdateConfigFile },
    { method: 'DELETE', pattern: /\/v1\/web\/game-config\/files\/([^/]+)$/,                      handler: handleDeleteConfigFile },
    { method: 'GET',    pattern: /\/v1\/web\/game-config\/files/,                                 handler: handleGetConfigFiles },
    { method: 'POST',   pattern: /\/v1\/web\/game-config\/files/,                                 handler: handleCreateConfigFile },
    { method: 'POST',   pattern: /\/v1\/web\/game-config\/branches\/([^/]+)\/publish$/,         handler: handlePublishConfigBranch },
    { method: 'POST',   pattern: /\/v1\/web\/game-config\/branches\/([^/]+)\/pause$/,           handler: handlePauseConfigBranch },
    { method: 'POST',   pattern: /\/v1\/web\/game-config\/branches\/([^/]+)\/resume$/,          handler: handleResumeConfigBranch },
    { method: 'POST',   pattern: /\/v1\/web\/game-config\/branches\/([^/]+)\/supersede$/,       handler: handleSupersedeConfigBranch },
    { method: 'POST',   pattern: /\/v1\/web\/game-config\/branches\/([^/]+)\/save-preview$/,     handler: handlePreviewConfigSave },
    { method: 'POST',   pattern: /\/v1\/web\/game-config\/branches\/([^/]+)\/dismiss-review$/,   handler: handleDismissConfigReview },
    { method: 'POST',   pattern: /\/v1\/web\/game-config\/branches\/([^/]+)\/rollback\/([^/]+)/, handler: handleRollbackConfigBranch },
    { method: 'PUT',    pattern: /\/v1\/web\/game-config\/branches\/([^/]+)\/rollout$/,         handler: handleUpdateConfigRollout },
    { method: 'GET',    pattern: /\/v1\/web\/game-config\/branches\/([^/]+)\/versions$/,        handler: handleGetConfigBranchVersions },
    { method: 'GET',    pattern: /\/v1\/web\/game-config\/branches\/([^/]+)$/,                   handler: handleGetConfigBranch },
    { method: 'PUT',    pattern: /\/v1\/web\/game-config\/branches\/([^/]+)$/,                   handler: handleSaveConfigBranch },
    { method: 'DELETE', pattern: /\/v1\/web\/game-config\/branches\/([^/]+)$/,                   handler: handleDeleteConfigBranch },

    // ── Game Management ──────────────────────────────────────────────
    // Secret management (must come before generic game-mgmt patterns)
    { method: 'GET',    pattern: /\/v1\/web\/game-mgmt\/([^/]+)\/([^/]+)\/secrets$/,            handler: handleGetSecretKeys },
    { method: 'POST',   pattern: /\/v1\/web\/game-mgmt\/([^/]+)\/([^/]+)\/rotate-secret/,      handler: handleRotateSecret },
    { method: 'DELETE', pattern: /\/v1\/web\/game-mgmt\/([^/]+)\/([^/]+)\/secrets\/([^/]+)/,    handler: handleRevokeSecret },
    // State management
    { method: 'POST',   pattern: /\/v1\/web\/game-mgmt\/([^/]+)\/suspend/,                     handler: handleSuspendGame },
    { method: 'POST',   pattern: /\/v1\/web\/game-mgmt\/([^/]+)\/activate/,                    handler: handleActivateGame },
    { method: 'POST',   pattern: /\/v1\/web\/game-mgmt\/([^/]+)\/sunset/,                      handler: handleSunsetGame },
    { method: 'GET',    pattern: /\/v1\/web\/game-mgmt\/game-client-id\/([^/]+)/,               handler: handleGetGameByClientId },
    { method: 'GET',    pattern: /\/v1\/web\/game-mgmt\/orgId\/([^/]+)/,                        handler: handleGetGamesByOrgId },
    { method: 'GET',    pattern: /\/v1\/web\/game-mgmt\/id\/([^/]+)/,                           handler: handleGetGameById },
    { method: 'PUT',    pattern: /\/v1\/web\/game-mgmt\/([^/]+)$/,                              handler: handleUpdateGame },
    { method: 'DELETE', pattern: /\/v1\/web\/game-mgmt\/([^/]+)$/,                              handler: handleDeleteGame },
    { method: 'GET',    pattern: /\/v1\/web\/game-mgmt/,                                        handler: handleGetGames },
    { method: 'POST',   pattern: /\/v1\/web\/game-mgmt/,                                        handler: handleCreateGame },

    // ── Org Management ───────────────────────────────────────────────
    { method: 'PATCH',  pattern: /\/v1\/web\/org-mgmt\/([^/]+)\/archive/,                       handler: handleArchiveOrg },
    { method: 'GET',    pattern: /\/v1\/web\/org-mgmt\/([^/]+)$/,                               handler: handleGetOrgById },
    { method: 'PUT',    pattern: /\/v1\/web\/org-mgmt/,                                         handler: handleUpdateOrg },
    { method: 'GET',    pattern: /\/v1\/web\/org-mgmt/,                                         handler: handleGetOrgs },
    { method: 'POST',   pattern: /\/v1\/web\/org-mgmt/,                                         handler: handleCreateOrg },

    // ── User Management ──────────────────────────────────────────────
    { method: 'GET',    pattern: /\/v1\/web\/user\/all/,                                        handler: handleGetAllUsers },
    { method: 'GET',    pattern: /\/v1\/web\/user\/user-pref-list/,                             handler: handleGetUserPreferences },
    { method: 'GET',    pattern: /\/v1\/web\/user$/,                                            handler: handleGetCurrentUser },
    { method: 'PUT',    pattern: /\/v1\/web\/invitation\/([^/]+)$/,                             handler: handleUpdateInvitation },
    { method: 'DELETE', pattern: /\/v1\/web\/invitation\/([^/]+)$/,                             handler: handleDeleteInvitation },
    { method: 'GET',    pattern: /\/v1\/web\/invitation/,                                       handler: handleGetInvitations },
    { method: 'POST',   pattern: /\/v1\/web\/invitation/,                                       handler: handleCreateInvitation },

    // ── Dashboard / Portfolio ────────────────────────────────────────
    { method: 'GET',    pattern: /\/v1\/web\/dashboards\/user\/layout/,                       handler: handleGetPortfolio },
    { method: 'GET',    pattern: /\/v1\/web\/user-game/,                                        handler: handleGetUserGames },
    { method: 'GET',    pattern: /\/v1\/web\/game-ecosystem\/companies\/([^/]+)/,                handler: handleGetCompanyByOrgId },
    { method: 'GET',    pattern: /\/v1\/web\/game-ecosystem\/company-games/,                     handler: handleGetCompanyGames },
    { method: 'GET',    pattern: /\/v1\/web\/live-services-homepage/,                            handler: handleGetLiveServicesHomepage },

    // ── Tags ─────────────────────────────────────────────────────────
    { method: 'DELETE', pattern: /\/v1\/web\/tags\/([^/]+)$/,                                   handler: handleDeleteTag },
    { method: 'GET',    pattern: /\/v1\/web\/tags/,                                             handler: handleGetTags },
    { method: 'POST',   pattern: /\/v1\/web\/tags/,                                             handler: handleCreateTag },

    // ── Common / Misc ────────────────────────────────────────────────
    { method: 'GET',    pattern: /\/v1\/web\/role/,                                             handler: handleGetRoles },
    { method: 'GET',    pattern: /\/api\/v1\/roles/,                                            handler: handleGetRoles },
    { method: 'GET',    pattern: /\/v1\/web\/platforms/,                                        handler: handleGetPlatforms },
    { method: 'GET',    pattern: /\/v1\/web\/metric-catalog/,                                   handler: handleGetMetrics },
    { method: 'GET',    pattern: /\/v1\/web\/permissions/,                                      handler: handleGetPermissions },
    { method: 'GET',    pattern: /\/v1\/web\/page-registry/,                                    handler: handleGetPages },
];

// ── Interceptor registration ─────────────────────────────────────────
let registered = false;

export function registerPlaygroundInterceptor() {
    if (registered) return;
    registered = true;

    axios.interceptors.request.use(
        (config) => {
            if (sessionStorage.getItem('playgroundMode') !== 'true') {
                return config;
            }

            const method = (config.method || 'GET').toUpperCase();
            const url = config.url || '';

            for (const route of routeTable) {
                if (route.method === method) {
                    const match = url.match(route.pattern);
                    if (match) {
                        try {
                            const responseData = route.handler(config, match);
                            config.adapter = () => Promise.resolve({
                                data: responseData,
                                status: 200,
                                statusText: 'OK',
                                headers: {},
                                config,
                            });
                        } catch (err) {
                            // Return error as a 400 response (for limit errors, etc.)
                            config.adapter = () => Promise.reject({
                                response: {
                                    status: 400,
                                    data: { message: err.message || 'Playground error' },
                                },
                                config,
                            });
                        }
                        return config;
                    }
                }
            }

            // Unmatched route in playground mode — return empty success to prevent real network call
            config.adapter = () => Promise.resolve({
                data: {},
                status: 200,
                statusText: 'OK',
                headers: {},
                config,
            });
            return config;
        },
        (error) => Promise.reject(error)
    );
}
